// =============================================================================
// forecastReader.js — the kitchen forecast screen, built from the Supabase engine.
//
// GATED OFF. FORECAST_READ_HOSTS is empty, so every tablet still asks the Google
// Apps Script forecast exactly as it does today. Switching a shop on is a reviewed
// one-line host add; taking it back off is deleting that line (or ?forecastread=off).
//
// WHY THIS FILE EXISTS. The forecast ENGINE is the last module living 100% on
// Google Apps Script, and it is the reason the estate's last Google-Sheet WRITE
// (the EOD report's syncToQuantitiesSheet_) can't be switched off. Moving the
// engine is a BUILD, not a flip (RUNBOOK_FORECASTER_READFLIP.md:107):
//   * every forecast RPC is service_role only — a tablet can call none of them;
//   * forecast_calc does not produce the per-roll split the screen renders.
// So the Supabase path is: a narrow authenticated inputs reader (migration 1120),
// the proven JS engine in the browser (src/lib/forecast.js — agrees with the live
// engine 177/177), an adapter to the EXACT shape SiteForecastSection already reads
// (forecastReaderCore.js), and a freshness guard + Google fallback so the worst
// case is today's behaviour.
//
// THE SHEET (Google) STAYS THE SAFETY NET. Any failure at all — no session, a
// network blip, a stale bench, a bad shape — falls straight back to the same
// ?action=forecast call the screen makes today.
//
// SCOPE: Manchester V2 / site 'tc' (Trafford; Arndale closed 16 Aug but rendered).
// Meadowhall runs the separate v4.5 engine and is OUT OF SCOPE — never route it here.
//
// HONESTY (staging parity tools/lane_bg_forecast_parity.mjs + an adversarial refute
// pass, night of 12/13 Sep 2026): the Supabase-built forecast does NOT yet equal
// the live engine — the arithmetic is forecast.js (proven 177/177), but the live
// engine leans on live signals the twin does not have. FIVE named flip blockers,
// all in RUNBOOK_FORECAST_ENGINE_FLIP.md: B1 no 2025 demand (prev-year absent);
// B2 forecast.js's school-holiday calendar disagrees with the live engine; B3 no
// WEATHER feed (open-meteo — a forward rainy/sunny day is materially off); B4 no
// BANK-HOLIDAY calendar (gov.uk); B5 newly-launched rolls with no history are
// dropped from the split. That is exactly why this ships gated OFF and must not be
// armed for forward days until those are resolved + a prod parity soak that
// INCLUDES forward days is clean.
// =============================================================================
import { supabase } from "./supabaseClient.js";
import { ensureAnonSession, ensurePass } from "./stockSync.js";
import { canonicalSite } from "./forecast.js";
import {
  buildForecastFromBundle, looksLikeAForecast, isBenchFreshEnough, forecastReadOverride, shadowDisagrees, kioskStatusIsAnswer,
} from "./forecastReaderCore.js";

// Re-export the pure builder + validators so screen code has one import site.
export { buildForecastFromBundle, looksLikeAForecast, isBenchFreshEnough, forecastReadOverride, shadowDisagrees, kioskStatusIsAnswer };

// --- the gate ---------------------------------------------------------------
// Ships EMPTY. The Trafford host (trafford.eatrolled.com — the kitchen is on site
// there since 24 Sep 2026; was the kitchen tablet's own host) is added here only
// after the input feeds are armed and a prod parity soak is clean
// (RUNBOOK_FORECAST_ENGINE_FLIP.md). Taking it back off is deleting the line.
const FORECAST_READ_HOSTS = new Set([]);

export function forecastReadEnabled() {
  if (typeof window === "undefined") return false;
  const o = forecastReadOverride(window.location.search);
  if (o !== null) return o;
  return FORECAST_READ_HOSTS.has((window.location.hostname || "").toLowerCase());
}

let _warnedStale = false;

// Lane BQ (13 Sep 2026): the two LIVE signals the engine adds on top of the
// date-derived flags — the gov.uk bank-holiday calendar (migration 1320) and the
// target day's weather (1321, or the engine's own open-meteo call when no stored
// reading exists). Only ever reached on a gated host. Sits below the import block on
// purpose: open PR #337 edits that block, and this keeps the two mergeable in either
// order — fold it into the block above once #337 has landed.
import { loadForecastSignals } from "./forecastSignals.js";

// One shop, one day. `googleFetch` is a thunk returning the live ?action=forecast
// JSON (the existing screen call), used as the fallback on ANY failure.
export async function fetchForecastDay({ site, date, googleFetch }) {
  // Always resolves to { data, source }. `source` is 'supabase' | 'supabase_fallback'
  // | 'google'; `data` is the forecast JSON (or null if even Google failed). The
  // caller renders `data` exactly as today and shows the source badge.
  const fallback = async (why, isError) => {
    if (isError) console.warn("[forecast-reader] fell back to the Google forecast:", why);
    try { return { data: await googleFetch(), source: "google" }; }
    catch (e) { return { data: null, source: "google" }; }
  };

  // Meadowhall runs v4.5 — never build it from this V2 reader.
  if (canonicalSite(site) === "Meadowhall") return fallback("meadowhall is v4.5 — out of scope", false);

  try { await ensureAnonSession(); }
  catch (e) { return fallback("no session: " + (e?.message || e), true); }

  let bundle;
  try {
    const res = await supabase.rpc("forecast_kiosk_inputs", { p_site: site, p_date: date });
    if (res.error) throw res.error;
    bundle = res.data;
  } catch (e) {
    return fallback("supabase error: " + (e?.message || e), true);
  }

  if (!bundle || bundle.error) return fallback("reader returned " + (bundle?.error || "null"), true);

  // A frozen/stale bench must never forecast a real kitchen.
  if (!isBenchFreshEnough(bundle, date)) {
    if (!_warnedStale) { console.warn("[forecast-reader] bench stale — using the Google forecast"); _warnedStale = true; }
    return fallback("bench stale (sales_last_date " + (bundle?.freshness?.sales_last_date || "?") + ")", false);
  }

  let built;
  try {
    // The bank-holiday list + this day's weather ride the engine config, exactly
    // where the live engine consults them (target flags AND the history's
    // de-normalising). loadForecastSignals never throws: a signal that cannot be
    // fetched is the engine's own "no flag" (gov.uk / open-meteo down).
    const signals = await loadForecastSignals({ site, date });
    built = buildForecastFromBundle(bundle, signals.config);
  } catch (e) {
    return fallback("build error: " + (e?.message || e), true);
  }

  if (!looksLikeAForecast(built)) return fallback("unexpected shape", true);
  return { data: built, source: built.dataSource === "supabase_fallback" ? "supabase_fallback" : "supabase" };
}

// =============================================================================
// Wave 2 — the lifecycle on gated hosts: status read + dual-written confirm/undo.
//
// The Sheet (Apps Script) stays the record until the engine flip day. On a gated
// host the screen ALSO reads the status from Supabase and, after a successful
// Sheet write, mirrors the confirm/undo into the Supabase doors (1121) as an
// honest secondary — it never blocks the Sheet path and never shows a Supabase
// success the Sheet refused. `date` is ISO (YYYY-MM-DD); the screen builds the
// en-GB string only for the Google status call. `staffPin` is used ONLY to obtain
// the login pass (ensurePass) — the doors read identity from the JWT, so NO name
// and NO PIN ever go in the RPC args.
// =============================================================================

// Status read (ISO date). Returns the exact ?action=forecaststatus shape, or null
// on anything unexpected — INCLUDING a bare {confirmed:false} (no row mirrored
// yet) — so the caller falls back to the Google status while the Sheet is the
// record (kioskStatusIsAnswer).
export async function kioskStatus({ site, date }) {
  if (!forecastReadEnabled()) return null;
  if (canonicalSite(site) === "Meadowhall") return null;
  try {
    await ensureAnonSession();
    const res = await supabase.rpc("forecast_kiosk_status", { p_site: site, p_date: date });
    if (res.error || !kioskStatusIsAnswer(res.data)) return null;
    return res.data;
  } catch (e) { return null; }
}

async function kioskWrite(rpc, args, { staffPin, site }) {
  if (!forecastReadEnabled()) return { status: "off" };
  if (canonicalSite(site) === "Meadowhall") return { status: "off" };
  try {
    const pass = await ensurePass(staffPin, site);
    if (pass?.skipped) return { status: "skipped" };   // dual-run off on this host
    if (!pass?.ok) return { status: "no_pass" };
    const res = await supabase.rpc(rpc, args);
    if (res.error) return { status: "error", reason: res.error.message };
    return res.data || { status: "error", reason: "no data" };
  } catch (e) { return { status: "error", reason: e?.message || String(e) }; }
}

// Mirror a confirm into forecast_kiosk_confirm. `override` is the % number the
// screen holds; it maps to overridePct (the door takes no free-text note).
export function kioskConfirm({ site, date, portions, rolls, override, skuAdjustments, snapshot, idemKey, staffPin }) {
  const p = {
    site, date,
    portions, rolls,
    overridePct: Number(override) || 0,
    skuAdjustments: skuAdjustments || {},
    snapshot: snapshot || {},
    idemKey: idemKey || null,
  };
  return kioskWrite("forecast_kiosk_confirm", { p }, { staffPin, site });
}

export function kioskUndo({ site, date, idemKey, staffPin }) {
  return kioskWrite("forecast_kiosk_undo", { p: { site, date, idemKey: idemKey || null } }, { staffPin, site });
}

// Total Made for the EOD Insights screen (ISO date). Resolves to a number, or null
// exactly as the Google read does. On a non-gated host, a non-fresh/absent day, or
// ANY failure it falls back to the Google ?action=totalmade (via googleFetch), so
// the worst case is today's behaviour. Never throws. (1122's `fresh` is the exact-
// day guard — the twin is a snapshot until the daily Quantities refresh is armed.)
export async function kioskTotalMade(site, date, { googleFetch } = {}) {
  const google = async () => { try { return googleFetch ? await googleFetch() : null; } catch (e) { return null; } };
  if (!forecastReadEnabled()) return google();
  if (canonicalSite(site) === "Meadowhall") return google();
  try {
    await ensureAnonSession();
    const res = await supabase.rpc("forecast_kiosk_total_made", { p_site: site, p_date: date });
    if (res.error || !res.data || res.data.error || !res.data.fresh || typeof res.data.totalMade !== "number") return google();
    return res.data.totalMade;
  } catch (e) { return google(); }
}
